import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Users, UserPlus, Search, GraduationCap } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const GroupList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const { data: groupsResponse, refresh, loading } = useGet(`/groups?page=${currentPage}`);
    
    // Resilient data extraction
    const groups = Array.isArray(groupsResponse) ? groupsResponse : (groupsResponse?.data || []);
    const pagination = groupsResponse?.meta || null;
    const { hasPermission } = useAuth();
    
    // Using school-classes permission as a fallback if 'groups' permission is not yet defined in the backend/seeder
    const canCreate = hasPermission('groups', 'create') || hasPermission('school-classes', 'create');
    const canDelete = hasPermission('groups', 'delete') || hasPermission('school-classes', 'delete');


    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isStudentsModalOpen, setIsStudentsModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [modalStudents, setModalStudents] = useState([]);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalPage, setModalPage] = useState(1);
    const [onlyUnassigned, setOnlyUnassigned] = useState(false);
    const [modalMeta, setModalMeta] = useState(null);
    const { success, error: toastError, warning } = useToast();

    const handleAdd = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/groups', { name });
            success('تم إضافة المجموعة بنجاح');
            setName(''); 
            refresh();
        } catch (err) { 
            toastError('خطأ أثناء الإضافة'); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف هذه المجموعة؟')) {
            try {
                await api.delete(`/groups/${id}`);
                success('تم حذف المجموعة بنجاح');
                refresh();
            } catch (err) {
                if (err.response?.data?.message) {
                    toastError(err.response.data.message);
                } else {
                    toastError('خطأ أثناء الحذف');
                }
            }
        }
    };

    useEffect(() => {
        if (isStudentsModalOpen) {
            fetchModalStudents();
        }
    }, [isStudentsModalOpen, modalPage, searchTerm, onlyUnassigned]);

    const fetchModalStudents = async () => {
        setStudentsLoading(true);
        try {
            const res = await api.get(`/students?page=${modalPage}&search=${searchTerm}&without_group=${onlyUnassigned}`);
            const data = res.data?.data || res.data || [];
            setModalStudents(data);
            setModalMeta(res.data?.meta || null);
        } catch (error) {
            console.error(error);
        } finally {
            setStudentsLoading(false);
        }
    };

    const openStudentsModal = async (group) => {
        setSelectedGroup(group);
        setSearchTerm('');
        setModalPage(1);
        setOnlyUnassigned(false);
        setIsStudentsModalOpen(true);
        setStudentsLoading(true);
        try {
            // Fetch students already in this group
            const res = await api.get(`/students?all=true&group_id=${group.id}`);
            const data = res.data?.data || res.data || [];
            setSelectedStudentIds(data.map(s => s.id));
        } catch (error) {
            console.error(error);
        }
    };

    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [allSelectedFromDB, setAllSelectedFromDB] = useState(false);

    const toggleAll = () => {
        const currentPageIds = modalStudents.map(s => s.id);
        const allOnPageSelected = currentPageIds.every(id => selectedStudentIds.includes(id));

        if (allOnPageSelected) {
            setSelectedStudentIds(prev => prev.filter(id => !currentPageIds.includes(id)));
            setAllSelectedFromDB(false);
        } else {
            const newIds = [...selectedStudentIds];
            currentPageIds.forEach(id => {
                if (!newIds.includes(id)) {
                    newIds.push(id);
                }
            });
            setSelectedStudentIds(newIds);
        }
    };

    const selectAllDatabase = async () => {
        setIsSelectingAll(true);
        try {
            const res = await api.get(`/students?all=true&search=${searchTerm}`);
            const allStudents = res.data?.data || res.data || [];
            setSelectedStudentIds(allStudents.map(s => s.id));
            setAllSelectedFromDB(true);
            success(`تم تحديد ${allStudents.length} طالب بنجاح`);
        } catch (error) {
            console.error(error);
            toastError('خطأ أثناء تحديد الكل');
        } finally {
            setIsSelectingAll(false);
        }
    };

    const toggleStudentSelection = (studentId) => {
        setSelectedStudentIds(prev => 
            prev.includes(studentId) 
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
        if (allSelectedFromDB) setAllSelectedFromDB(false);
    };

    const handleAssignStudents = async () => {
        if (!selectedGroup) return;
        setIsSubmitting(true);
        try {
            await api.post(`/groups/${selectedGroup.id}/assign-students`, {
                student_ids: selectedStudentIds
            });
            success('تم تعيين التلاميذ بنجاح');
            setIsStudentsModalOpen(false);
            refresh();
        } catch (error) {
            toastError('خطأ أثناء تعيين التلاميذ');
        } finally {
            setIsSubmitting(false);
        }
    };
    

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-800">إدارة المجموعات</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1 font-medium">تنظيم الطلاب في مجموعات مخصصة</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                    <StudentLookup />
                    <div className="flex bg-white px-4 py-2 rounded-lg border border-slate-100 items-center gap-2 h-11">
                        <Users className="text-primary w-5 h-5" />
                        <span className="text-sm font-semibold text-slate-600">إجمالي المجموعات: {pagination?.total || 0}</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Section */}
                {canCreate ? (
                    <Card className="p-4 h-fit lg:col-span-1 border-primary/10 bg-orange-50/10">
                        <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-primary" /> إضافة مجموعة جديدة
                        </h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <Input 
                                label="اسم المجموعة" 
                                value={name} 
                                onChange={e => setName(e.target.value)} 
                                placeholder="مثال: مجموعة أ" 
                                required 
                            />
                            <Button type="submit" isLoading={isSubmitting} className="w-full mt-2">
                                حفظ المجموعة
                            </Button>
                        </form>
                    </Card>
                ) : (
                    <Card className="p-8 h-fit lg:col-span-1 border-slate-100 bg-slate-50/50 text-center flex flex-col items-center justify-center">
                        <Users className="w-8 h-8 text-slate-300 mb-2" />
                        <p className="text-xs text-slate-400">لا تملك صلاحية إضافة مجموعات جديدة</p>
                    </Card>
                )}

                {/* List Section */}
                <Card className="lg:col-span-2 overflow-hidden border-slate-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">المعرف</th>
                                    <th className="px-6 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">اسم المجموعة</th>
                                    <th className="px-6 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">عدد التلاميذ</th>
                                    <th className="px-6 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-left">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-slate-400 animate-pulse">جاري التحميل...</td>
                                    </tr>
                                )}
                                {!loading && groups?.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-slate-400">لا توجد مجموعات مسجلة حالياً</td>
                                    </tr>
                                )}
                                {groups?.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="font-bold text-primary bg-primary/5 px-2 py-1 rounded text-sm">
                                                #{item.id}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 font-bold text-slate-800">{item.name}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1.5 text-slate-600">
                                                <Users className="w-4 h-4 text-slate-400" />
                                                <span className="font-medium text-sm">{item.students_count || 0}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-left">
                                            <div className="flex items-center gap-1">
                                                <button 
                                                    onClick={() => openStudentsModal(item)}
                                                    className="p-2 text-slate-400 hover:text-primary hover:bg-orange-50 rounded-lg transition-all"
                                                    title="إضافة تلاميذ"
                                                >
                                                    <UserPlus size={16}/>
                                                </button>
                                                {canDelete && (
                                                    <button 
                                                        onClick={() => handleDelete(item.id)} 
                                                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                        title="حذف المجموعة"
                                                    >
                                                        <Trash2 size={16}/>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination && pagination.last_page > 1 && (
                        <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                            <div className="text-xs text-slate-500 font-normal">
                                عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> مجموعة
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="h-8 px-3 text-xs"
                                >
                                    السابق
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                        let pageNum = i + 1;
                                        if (pagination.last_page > 5 && currentPage > 3) {
                                            pageNum = currentPage - 3 + i + 1;
                                            if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                                    currentPage === pageNum 
                                                    ? 'bg-primary text-white shadow-sm font-medium' 
                                                    : 'text-slate-500 hover:bg-slate-100'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                    disabled={currentPage === pagination.last_page}
                                    className="h-8 px-3 text-xs"
                                >
                                    التالي
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>

            {/* Modal for Assigning Students */}
            <Modal isOpen={isStudentsModalOpen} onClose={() => setIsStudentsModalOpen(false)} title={`تعيين التلاميذ للمجموعة: ${selectedGroup?.name}`}>
                <div className="space-y-4 pt-4">
                    <div className="flex flex-col sm:flex-row gap-3 items-center">
                        <div className="flex-1 w-full">
                            <Input 
                                placeholder="ابحث عن تلميذ بالاسم، رقم مسار، أو QR..." 
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setModalPage(1);
                                }}
                            />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 shrink-0">
                            <input 
                                type="checkbox" 
                                checked={onlyUnassigned} 
                                onChange={(e) => { setOnlyUnassigned(e.target.checked); setModalPage(1); }}
                                className="w-4 h-4 rounded text-primary focus:ring-primary"
                            />
                            <span className="text-xs font-bold text-slate-600">غير المسجلين فقط</span>
                        </label>
                    </div>
                    
                    {studentsLoading ? (
                         <div className="flex justify-center p-8"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div></div>
                    ) : (
                        <>
                        <div className="flex flex-col gap-2 px-1">
                            <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg border border-slate-100">
                                <span className="text-sm text-slate-500 font-medium pb-1">تم تحديد {selectedStudentIds.length} طالب</span>
                                <button 
                                    type="button" 
                                    onClick={async () => {
                                        if (allSelectedFromDB || (modalMeta && selectedStudentIds.length === modalMeta.total) || selectedStudentIds.length > 0) {
                                            setSelectedStudentIds([]);
                                            setAllSelectedFromDB(false);
                                        } else {
                                            await selectAllDatabase();
                                        }
                                    }}
                                    disabled={isSelectingAll}
                                    className="text-xs text-primary font-bold hover:underline bg-primary/10 px-3 py-1.5 rounded disabled:opacity-50"
                                >
                                    {isSelectingAll ? 'جاري التحديد...' : (allSelectedFromDB || (modalMeta && selectedStudentIds.length === modalMeta.total) || selectedStudentIds.length > 0) ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                                </button>
                            </div>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 mb-4">
                            {modalStudents.length === 0 ? (
                                <div className="p-4 text-center text-slate-500 text-sm">لا يوجد تلاميذ مطابقين</div>
                            ) : (
                                modalStudents.map(student => (
                                    <div key={student.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors">
                                        <input 
                                            type="checkbox" 
                                            checked={selectedStudentIds.includes(student.id)}
                                            onChange={() => toggleStudentSelection(student.id)}
                                            className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <p className="font-medium text-slate-800 text-sm">{student.full_name}</p>
                                                {student.group && student.group.id !== selectedGroup?.id && (
                                                    <span className="text-[9px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded border border-red-100 font-bold animate-in fade-in zoom-in-95">
                                                        في مجموعة: {student.group.name}
                                                    </span>
                                                )}
                                                {student.group && student.group.id === selectedGroup?.id && (
                                                    <span className="text-[9px] bg-green-50 text-green-500 px-1.5 py-0.5 rounded border border-green-100 font-bold animate-in fade-in zoom-in-95">
                                                        في هذه المجموعة
                                                    </span>
                                                )}
                                                {!student.group && (
                                                     <span className="text-[9px] bg-slate-50 text-slate-400 px-1.5 py-0.5 rounded border border-slate-100">
                                                        غير مسجل
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500">{student.code_massar}</p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        </>
                    )}

                    {/* Modal Pagination */}
                    {modalMeta && modalMeta.last_page > 1 && (
                        <div className="flex items-center justify-between pb-4">
                            <span className="text-xs text-slate-500">صفحة {modalPage} من {modalMeta.last_page}</span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setModalPage(p => Math.max(1, p - 1))} disabled={modalPage === 1}>السابق</Button>
                                <Button size="sm" variant="outline" onClick={() => setModalPage(p => Math.min(modalMeta.last_page, p + 1))} disabled={modalPage === modalMeta.last_page}>التالي</Button>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-2 pt-4">
                        <Button onClick={handleAssignStudents} isLoading={isSubmitting} className="flex-1">
                            حفظ التعيينات ({selectedStudentIds.length})
                        </Button>
                        <Button variant="outline" onClick={() => setIsStudentsModalOpen(false)} className="flex-1" disabled={isSubmitting}>
                            إلغاء
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const StudentLookup = () => {
    const [search, setSearch] = useState('');
    const [student, setStudent] = useState(null);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState('');

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        if (!search.trim()) return;

        setSearching(true);
        setError('');
        setStudent(null);
        try {
            const res = await api.get(`/students?all=true&search=${search}`);
            const data = res.data?.data || res.data || [];
            if (data.length > 0) {
                // Return the first exact match if possible, else first result
                const found = data.find(s => s.qr_code === search || s.code_massar === search) || data[0];
                setStudent(found);
            } else {
                setError('لم يتم العثور على التلميذ');
            }
        } catch (err) {
            setError('حدث خطأ أثناء البحث');
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="relative w-full sm:w-80 lg:w-96 shrink-0 order-first sm:order-none">
            <div className="relative flex items-center bg-white border border-slate-200 rounded-xl overflow-hidden h-11 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all shadow-sm">
                <div className="flex-1 flex items-center pr-3 min-w-0">
                    <Search size={16} className="text-slate-400 shrink-0 ml-2" />
                    <input 
                        placeholder="ابحث عن تلميذ (اسم أو QR)..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-full h-full py-2 outline-none text-sm bg-transparent"
                    />
                </div>
                <button 
                    onClick={handleSearch}
                    disabled={searching}
                    className="h-full px-5 bg-primary text-white text-xs font-bold hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
                >
                    {searching ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : null}
                    <span>بحث</span>
                </button>
            </div>

            {student && (
                <Card className="absolute top-full left-0 right-0 mt-2 p-3 bg-white shadow-xl border-slate-100 z-50 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-slate-800">{student.full_name}</span>
                            <button onClick={() => setStudent(null)} className="text-slate-400 hover:text-red-500">×</button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="bg-slate-100 px-1.5 py-0.5 rounded font-mono">{student.code_massar}</span>
                            <span>•</span>
                            <span>{student.school_class?.name || '--'}</span>
                        </div>
                        <div className={`mt-1 p-2 rounded-lg flex items-center gap-2 border ${
                            student.group 
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700' 
                                : 'bg-amber-50 border-amber-100 text-amber-700'
                        }`}>
                            <Users size={14} />
                            <span className="text-xs font-bold">
                                {student.group ? `المجموعة: ${student.group.name}` : 'غير مسجل في أي مجموعة'}
                            </span>
                        </div>
                    </div>
                </Card>
            )}

            {error && (
                <div className="absolute top-full left-0 right-0 mt-2 p-2 bg-red-50 text-red-500 text-[10px] rounded border border-red-100 italic z-50">
                    {error}
                </div>
            )}
        </div>
    );
};

export default GroupList;
